import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IonContent, IonModal } from "@ionic/react";
import { useTranslation } from "react-i18next";
import ContentRenderer from "../../components/organisms/ContentRenderer/ContentRenderer";
import { useConfig } from "../../contexts/ConfigContext";
import { getVideoDescription, VideoMeta } from "../../services/video.service";
import { RichContentBlock } from "../../types/richContent";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  video?: VideoMeta;
};

/**
 * Volet « Description » ouvert depuis le bouton « Plus » du lecteur.
 *
 * Le corps est le contenu enrichi rédigé dans Diapason (page Vidéos) : ce sont les
 * mêmes blocs que ceux d'un article, rendus par ContentRenderer — donc le même design
 * system, le même thème d'étude et les mêmes traductions. La vidéo elle-même ne porte
 * que le teaser d'une ligne dérivé de ces blocs (meta.description, affiché en overlay
 * sur le lecteur).
 */
const VideoDescriptionPanel: React.FC<Props> = ({ isOpen, onClose, video }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { study } = useConfig();

  const [blocks, setBlocks] = useState<RichContentBlock[] | null>(null);

  const title = video?.meta?.title || video?.meta?.name || "";
  const uid = video?.uid;

  // Chargé à l'ouverture seulement : la description d'une vidéo jamais ouverte n'a
  // pas à coûter une requête pendant le défilement du lecteur.
  useEffect(() => {
    if (!isOpen || !study?.id || !uid) return undefined;

    let active = true;
    setBlocks(null);

    getVideoDescription(study.id, uid, language)
      .then((data) => {
        if (active) setBlocks(data?.content?.blocks ?? []);
      })
      .catch(() => {
        // Pas de description = état normal ; un échec réseau se lit pareil ici
        // (le volet reste ouvert, simplement vide).
        if (active) setBlocks([]);
      });

    return () => {
      active = false;
    };
  }, [isOpen, study?.id, uid, language]);

  return (
    <IonModal
      // --scroll: an authored description has no length limit, so this sheet must
      // scroll its body instead of sizing itself to the content (theme/modal.scss).
      className="resonance-sheetmodal resonance-sheetmodal--scroll"
      isOpen={isOpen}
      backdropDismiss
      onWillDismiss={onClose}
      initialBreakpoint={1}
      breakpoints={[0, 1]}
      canDismiss
    >
      {/* Header + ion-content as DIRECT children of the modal: that is the structure
          Ionic's sheet lays out (and looks the ion-content up in) — an extra wrapper
          between them is what left the body unscrollable. */}
      <Header>
        <HeaderTitle>{t("mentalplus_video_description")}</HeaderTitle>
        <CloseButton onClick={onClose} aria-label={t("common_close")}>
          ✕
        </CloseButton>
      </Header>

      <Body>
        {!!title && <VideoTitle>{title}</VideoTitle>}

        {blocks === null ? null : blocks.length ? (
          <ContentRenderer data={{ blocks }} />
        ) : (
          <EmptyState>{t("mentalplus_video_description_empty")}</EmptyState>
        )}
      </Body>
    </IonModal>
  );
};

export default VideoDescriptionPanel;

/* -------------------------------------------------------------------------- */
/*                                   STYLES                                   */
/* -------------------------------------------------------------------------- */

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 20px 12px;
  flex: 0 0 auto;
  background: var(--app-color-background, #ffffff);
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--app-color-text, #041e32);
`;

const CloseButton = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  font-size: 18px;
  color: var(--app-color-text, #041e32);
  cursor: pointer;
  padding: 4px;
`;

/* The scrolling body. Insets go through Ionic's own --padding-* variables rather than
   CSS padding: ion-content paints and scrolls an inner shadow root, so padding set from
   outside would clip the scroll area instead of insetting its content. */
const Body = styled(IonContent)`
  flex: 1;
  min-height: 0;
  --background: var(--app-color-background, #ffffff);
  --padding-top: 4px;
  --padding-start: 20px;
  --padding-end: 20px;
  --padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
`;

const VideoTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 700;
  color: var(--app-color-text, #041e32);
  line-height: 1.3;
`;

const EmptyState = styled.p`
  margin: 0;
  font-size: 14px;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.45);
`;
